import {
  type IInfoPopoverProps,
  InfoPopover,
} from '@framework/ui/components/popover/InfoPopover.tsx';
import { Form, type FormItemProps } from 'antd';
import clx from 'classnames';
import {
  Children,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { type FieldPath, type FieldValues } from 'react-hook-form';
import styled from 'styled-components';
import { useField } from '../field/useField.tsx';
import { type LeafError } from '../form/errors/LeafError.tsx';
import { useFormSectionContext } from '../form/section/FormSectionContext.tsx';
import { FormItemContext } from './FormItemContext.tsx';
import { getAllFormItemErrors } from './getAllFormItemErrors.tsx';
import { FormItemAccessibilityLabel } from './label/FormItemAccessibilityLabel.tsx';
import { isBooleanCheckboxElement } from './symbols/isBooleanCheckboxElement.tsx';
import { isRadioOrCheckboxGroupElement } from './symbols/isRadioOrCheckboxGroupElement.tsx';

export interface FormItemLayout {
  removeMargin?: boolean;
  removeLabelSpace?: boolean;
  removeHelpMargin?: boolean;
  hideVisualLabel?: boolean;
  /** Etichetta "a blocco": nel flusso, a piena larghezza e su più righe */
  labelLayoutBlock?: boolean;
}

export const StyledFormItem = styled(Form.Item)`
  &.remove-label-space {
    .form-handler & {
      padding-top: 0;
    }
    // per input tabella (spostato in TableInputWidgetRowsTable)
    .ant-radio-wrapper,
    .ant-checkbox-wrapper {
      .form-handler & {
        /* margin-top: 0; */
      }
    }
  }
  &.remove-help-margin {
    margin-bottom: 0;
  }
  /* Per l'accessibilità è necessario che il label sia presente nell'html */
  &.hide-visual-label {
    .ant-form-item-label label {
      .form-handler & {
        position: absolute;
        max-width: 1000px;
        left: -9999px;
        overflow-x: hidden;
      }
    }
  }
  /* Etichetta "a blocco": sostituisce la resa flottante di LayoutGlobalStyle
     (blocco autonomo: per spostarlo nel global basta cambiare il selettore radice).
     Il doppio && vince a parità di specificità sulla regola .ant-form-vertical. */
  &.label-layout-block {
    .form-handler && {
      padding-top: 0;

      .ant-form-item-label {
        position: static;
        margin-top: 0;
        height: auto;
        pointer-events: auto;

        > label {
          font-size: ${props => props.theme.fontSize}px;
          height: auto;
          padding: 0;
          background-color: transparent;
          display: block;
          white-space: normal;
          overflow: visible;
          margin-bottom: 0;
        }
      }

      /* Via la compensazione dell'etichetta flottante, ma il ritmo TRA le
         opzioni impilate (.vertical) resta quello del flottante (10px). */
      .ant-radio-wrapper,
      .ant-checkbox-wrapper {
        margin-top: 0;
      }
      .vertical {
        /* 5px = (controlHeight 32 − riga ~22)/2: pareggia centraggio opzioni */
        .ant-radio-wrapper,
        .ant-checkbox-wrapper  {
          margin-top: 5px;
        }
        .ant-radio-wrapper + .ant-radio-wrapper,
        .ant-checkbox-wrapper + .ant-checkbox-wrapper {
          margin-top: 10px;
        }
      }
    }
  }
  // per rimuovere l'asterisco ross nei campi booleani richiesti
  &.remove-requires-asterisk {
    .ant-form-item-label > label.ant-form-item-required::before {
      display: none;
    }
  }
  // per allineare i messaggi di errore e warning
  &.align-flex-end {
    .ant-form-item-explain-error {
      text-align: right;
    }
  }
  &.align-center {
    .ant-form-item-explain-error {
      text-align: center;
    }
  }
`;

function getValidateStatus(params: {
  isTouched: boolean;
  errors: LeafError[];
  warnings: string[];
  isSubmitted: boolean | undefined;
}) {
  const { isTouched, errors, warnings, isSubmitted } = params;
  // Se non è stato toccato e non è stato inviato, non mostriamo alcun feedback
  // In questo modo non mostriamo gli errori e i warning ai primi render, ma solo dopo un tentativo di invio
  if (!isTouched && !isSubmitted) return 'success' as const;
  if (errors.length > 0) return 'error' as const;
  if (warnings.length > 0) return 'warning' as const;
  return 'success' as const;
}

function getErrorsText(params: {
  validateStatus: 'error' | 'warning' | 'success';
  errors: LeafError[];
  warnings: string[];
}) {
  const { validateStatus, errors, warnings } = params;
  const hasError = validateStatus === 'error';
  const hasWarning = validateStatus === 'warning';

  return hasError
    ? errors.map(e => e.error.message).join(', ')
    : hasWarning
      ? warnings.join(', ')
      : undefined;
}

export interface IFormItemProps<T extends FieldValues = Record<string, any>>
  // Layout è stato introdotto anche da `antd`, ma noi usiamo già la prop internamente
  extends Omit<FormItemProps, 'layout'> {
  /** Se fornito un array, il form gestirà più campi contemporaneamente */
  name: FieldPath<T> | Array<FieldPath<T>> | undefined;
  children: ReactNode;
  layout?: FormItemLayout;
  /** Se inidcato, mostra soltanto gli errori di "primo livello", relativi specificatamente al campo e non ai suoi figli (e.g. liste ripetibili) */
  onlyFirstLevelErrors?: boolean;

  /** Se impostato a true, il campo del formItem è in sola lettura, e di conseguenza disabilito le configurazioni grafiche (feedback, status, messages) */
  readonly?: boolean;
  /**
   * Solo se inserito in un `RowElement`, permette di specificare il numero
   * di colonne
   */
  colSpan?: number;
  /**
   * Se impostato a true, l'asterisco verrà visualizzato quando il widget è .required().
   * Utile per quei campi booleani obbligatori dove è necessario spuntare il campo
   */
  allowsBooleanCheckboxAsterisk?: boolean;
  /**
   * Se true, forza il campo ad essere considerato "touched".
   * Utile per forzare lo stato di errore alla renderizzazione iniziale.
   */
  isAlwaysTouched?: boolean;
  /**
   * Visualizziamo il Widget come _disabled_ anche se non è fisicamente
   * disabilitato a livello di schema.
   * Deve essere usato in combinazione con la prop `readonly` per
   * disabilitare l'input a livello di UI ma mantenere il valore
   * visibile.
   */
  visuallyDisabled?: boolean;
  /**
   * Se impostato a true, mostra l'asterisco di campo obbligatorio.
   * Utile per mostrare l'asterisco anche nei campi disabilitati.
   */
  required?: boolean;
  /**
   * Aggiunge un'icona ⓘ accanto al label che apre un popover ricco al click.
   * Da usare per spiegazioni di 5-10 righe con titolo, paragrafi, esempi.
   *
   * Differenza con `tooltip` di Ant: il tooltip appare in hover con contenuto
   * breve (1 riga); il popover appare al click con contenuto strutturato.
   *
   * Le opzioni avanzate (icona custom, placement, aria-label dell'icona, ecc.)
   * sono passate direttamente a `InfoPopover` (estende `Popover` di Ant).
   */
  popover?: Omit<IInfoPopoverProps, 'content'> & {
    content: ReactNode;
  };
}

export function FormItem<T extends FieldValues = Record<string, any>>(
  props: IFormItemProps<T>,
) {
  const {
    name,
    children,
    className,
    help,
    onlyFirstLevelErrors,
    readonly,
    layout,
    allowsBooleanCheckboxAsterisk,
    isAlwaysTouched,
    visuallyDisabled,
    popover,
    required: requiredExplicit,
    ...itemProps
  } = props;

  const {
    error,
    visible,
    enabled,
    layoutEnabled,
    touched,
    unregister,
    getSchema,
    isSubmitted,
    getValues,
    required,
    warnings,
    isFieldStateStale,
  } = useField(name);

  const isTouched = Boolean(isAlwaysTouched) || touched.some(t => t);

  const errors = useMemo(() => {
    return getAllFormItemErrors(error).filter(e =>
      onlyFirstLevelErrors ? e.path.length === 0 : true,
    );
  }, [error, onlyFirstLevelErrors]);

  // Ottiene l'id per l'attributo aria-labelledby
  const ariaLabelId = useId();

  /**
   * Al cambio di visibilità, se il campo viene nascosto allora de-registriamo il campo
   * per evitare che il form mantenga il valore del campo nascosto.
   */
  useEffect(() => {
    /**
     * Se lo state è stale, evitiamo di eseguire la logica di unregister/register,
     * che potrebbe basarsi su uno state obsoleto.
     *  Aspettiamo il prossimo ciclo di aggiornamento dopo il flush delle notifiche.
     */
    if (isFieldStateStale()) {
      return;
    }
    if (!visible) {
      unregister();
      return;
    }

    // All'unmount, se lo schema prevede lo strip, de-registriamo il campo.
    // Serve ri-calcolarlo perché i valori potrebbero essere cambiati
    return () => {
      const actual = getSchema();
      if (actual?.spec?.strip) {
        unregister();
      }
    };
  }, [getSchema, unregister, visible, isFieldStateStale]);

  const [labelAddition, setLabelAddition] = useState<ReactNode | undefined>();

  const context = useMemo(
    () => ({
      ariaLabelId,
      errors,
      required,
      enabled,
      getSchema,
      warnings,
      layoutEnabled,
      readonly,
      visuallyDisabled,
      name: Array.isArray(name) ? name : [name],
      setLabelAddition,
    }),
    [
      ariaLabelId,
      errors,
      warnings,
      required,
      enabled,
      getSchema,
      layoutEnabled,
      visuallyDisabled,
      readonly,
      name,
    ],
  );

  const { validateStatus, errorsText, hasFeedback } = useMemo(() => {
    // Se il campo è in sola lettura, non mostriamo alcun feedback (manteniamo un layout "di default")
    if (readonly) {
      return {
        validateStatus: '' as const,
        errorsText: undefined,
        hasFeedback: false,
      };
    }
    const validateStatus = getValidateStatus({
      errors,
      isTouched,
      warnings,
      isSubmitted,
    });
    const errorsText = getErrorsText({ validateStatus, errors, warnings });

    const allValues = Object.values(getValues() ?? {});

    /**
     * Impostiamo il feedback (iconcina) ai campi solo se sono Touched o si è inviato il form.
     * Questo avviene perché se tenessimo il il "touched", i campi che _non_ sono stati "toccati"
     * durante la compilazione non avrebbero l'iconcina di errore.
     *
     * Inoltre, in base alla `mode` del FormHandler (onBlur, onChange, onSubmit), il "touched" potrebbe
     * non essere impostato: per esempio in caso di `onChange` viene impostato solo _alla modifica_ del campo
     * mentre in `onBlur` il touched viene impostato a true anche con un focus-unfocus del campo.
     *
     * Notare che il salvataggio in bozza _non_ imposta il "isSubmitted" (dato che utilizza un flusso di
     * submit custom che non passa direttamente per il formHandler).
     */
    const hasFeedback =
      itemProps.hasFeedback !== false &&
      (isTouched || isSubmitted) &&
      // Verifichiamo che tutti i valori siano definiti, altrimenti non mostriamo
      // il feedback
      (allValues.every((v: any) => v != null) || validateStatus !== 'success');

    return { validateStatus, errorsText, hasFeedback };
  }, [
    errors,
    isSubmitted,
    isTouched,
    itemProps.hasFeedback,
    readonly,
    warnings,
    getValues,
  ]);

  // Visualizzazione
  const sectionContext = useFormSectionContext();

  if (!visible) return null;

  const isRadioOrCheckboxGroup = Children.toArray(children).some(
    isRadioOrCheckboxGroupElement,
  );

  const isBooleanCheckbox = Children.toArray(children).some(
    isBooleanCheckboxElement,
  );

  return (
    <FormItemContext.Provider value={context}>
      <StyledFormItem
        hasFeedback={hasFeedback}
        validateStatus={validateStatus}
        extra={help}
        help={errorsText}
        required={required || requiredExplicit}
        {...itemProps}
        className={clx(className, {
          'remove-label-space':
            layout?.removeLabelSpace ?? layout?.removeMargin,
          'remove-help-margin':
            layout?.removeHelpMargin ??
            layout?.removeMargin ??
            sectionContext?.removeHelpMargin,
          'hide-visual-label': layout?.hideVisualLabel,
          'label-layout-block': layout?.labelLayoutBlock,
          'remove-requires-asterisk':
            (!allowsBooleanCheckboxAsterisk && isBooleanCheckbox) || readonly,
        })}
        htmlFor={isRadioOrCheckboxGroup ? undefined : ariaLabelId}
        label={
          isRadioOrCheckboxGroup ? (
            <>
              <FormItemAccessibilityLabel id={ariaLabelId}>
                {props.label}
                {labelAddition}
              </FormItemAccessibilityLabel>
              {popover && <InfoPopover {...popover} />}
            </>
          ) : (
            props.label && (
              <>
                {props.label}
                {popover && <InfoPopover {...popover} />}
                {labelAddition}
              </>
            )
          )
        }
      >
        {props.children}
      </StyledFormItem>
    </FormItemContext.Provider>
  );
}
